import _ from "lodash";
import { AddEmployeeReq, UpdateEmployeeChanges } from "../types/Employee";

const nullableStrings: (keyof AddEmployeeReq)[] = [
  "personalEmail",
  "subsidiaryCompanyId",
  "employeeDesignation",
  "grade",
  "contactNumber",
  "address",
  "notes",
];

type EmployeeTag = NonNullable<AddEmployeeReq["tags"]>[number];

export function normalizeEmployeeTags(value: unknown): EmployeeTag[] {
  const tags = Array.isArray(value)
    ? value.every((tag) => typeof tag === "string" && tag.length === 1)
      ? [value.join("")]
      : value
    : typeof value === "string"
    ? value.split(",")
    : [];

  return tags
    .flatMap((tag): EmployeeTag[] => {
      if (typeof tag === "string") {
        return tag
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .map((item) => ({ label: item, value: item }));
      }
      if (!tag || typeof tag !== "object") return [];

      const tagValue = tag as { label?: unknown; value?: unknown };
      const label = String(tagValue.label ?? tagValue.value ?? "").trim();
      const normalizedValue = String(
        tagValue.value ?? tagValue.label ?? ""
      ).trim();
      return normalizedValue
        ? [{ label: label || normalizedValue, value: normalizedValue }]
        : [];
    })
    .filter(
      (tag, index, normalizedTags) =>
        normalizedTags.findIndex((item) => item.value === tag.value) === index
    );
}

function canonicalTags(tags: unknown) {
  return normalizeEmployeeTags(tags).sort((a, b) =>
    a.value.localeCompare(b.value)
  );
}

function canonicalDemats(accounts: AddEmployeeReq["dematAccounts"] = []) {
  return accounts.map(({ s3Path: _s3Path, ...account }) => account);
}

function canonicalNominees(nominees: AddEmployeeReq["nominees"] = []) {
  return nominees
    .map(({ id: _id, guardian, ...nominee }) => ({
      ...nominee,
      name: nominee.name.trim(),
      relationship: nominee.relationship.trim(),
      contact: nominee.contact?.trim() || undefined,
      email: nominee.email?.trim().toLowerCase() || undefined,
      address: nominee.address?.trim() || undefined,
      percentage: Number(nominee.percentage),
      guardian: guardian
        ? {
            name: guardian.name.trim(),
            relationship: guardian.relationship.trim(),
            dateOfBirth: guardian.dateOfBirth,
            contact: guardian.contact?.trim() || undefined,
            email: guardian.email?.trim().toLowerCase() || undefined,
            address: guardian.address?.trim() || undefined,
            percentage: 0,
          }
        : undefined,
    }))
    .sort((a, b) =>
      `${a.name}:${a.relationship}:${a.dateOfBirth}`.localeCompare(
        `${b.name}:${b.relationship}:${b.dateOfBirth}`
      )
    );
}

export function buildEmployeePatch(
  initial: AddEmployeeReq,
  current: AddEmployeeReq,
  options: {
    customFieldInitial?: Record<string, any>;
    customFieldCurrent?: Record<string, any>;
    panCipher?: string;
    removePan?: boolean;
    profileImage?: UpdateEmployeeChanges["profileImage"];
  } = {}
): UpdateEmployeeChanges {
  const changes: UpdateEmployeeChanges = {};
  const scalarFields: (keyof AddEmployeeReq)[] = [
    "employeeName",
    "legalName",
    "email",
    "personalEmail",
    "employeeIdentification",
    "employmentType",
    "dateOfJoin",
    "subsidiaryCompanyId",
    "employeeDesignation",
    "department",
    "grade",
    "salary",
    "contactNumber",
    "address",
    "residentialStatus",
    "nationality",
    "notes",
  ];

  for (const field of scalarFields) {
    let oldValue: any = initial[field];
    let newValue: any = current[field];
    if (field === "email" || field === "personalEmail") {
      oldValue = String(oldValue || "").trim();
      newValue = String(newValue || "").trim();
    }
    if (field === "salary") {
      oldValue = oldValue === "" || oldValue == null ? null : Number(oldValue);
      newValue = newValue === "" || newValue == null ? null : Number(newValue);
    }
    if (nullableStrings.includes(field)) {
      oldValue = oldValue === "" || oldValue === undefined ? null : oldValue;
      newValue = newValue === "" || newValue === undefined ? null : newValue;
    }
    if (!_.isEqual(oldValue, newValue)) {
      (changes as any)[field] = newValue;
    }
  }

  const oldManagers = [...(initial.managerIds || [])].sort();
  const newManagers = [...(current.managerIds || [])].sort();
  if (!_.isEqual(oldManagers, newManagers)) changes.managerIds = newManagers;

  const oldTags = canonicalTags(initial.tags);
  const newTags = canonicalTags(current.tags);
  if (!_.isEqual(oldTags, newTags)) changes.tags = newTags;

  const oldDemats = canonicalDemats(initial.dematAccounts);
  const newDemats = canonicalDemats(current.dematAccounts);
  if (!_.isEqual(oldDemats, newDemats)) changes.dematAccounts = newDemats;

  const oldNominees = canonicalNominees(initial.nominees);
  const newNominees = canonicalNominees(current.nominees);
  if (!_.isEqual(oldNominees, newNominees)) changes.nominees = newNominees;

  const customFieldChanges: Record<string, unknown | null> = {};
  const oldCustom = options.customFieldInitial || {};
  const newCustom = options.customFieldCurrent || {};
  for (const key of new Set([
    ...Object.keys(oldCustom),
    ...Object.keys(newCustom),
  ])) {
    const oldValue = oldCustom[key] === "" ? null : oldCustom[key] ?? null;
    const newValue = newCustom[key] === "" ? null : newCustom[key] ?? null;
    if (!_.isEqual(oldValue, newValue)) customFieldChanges[key] = newValue;
  }
  if (Object.keys(customFieldChanges).length) {
    changes.customFieldChanges = customFieldChanges;
  }
  if (options.panCipher) changes.panCipher = options.panCipher;
  if (options.removePan) changes.removePan = true;
  if (options.profileImage) changes.profileImage = options.profileImage;
  return changes;
}
